// Right-to-left function composition: compose(f, g, h)(...args) === f(g(h(...args))).
// The overloads thread each function's return type into the next one's parameter, so
// HOC stacks — compose(withRouter, connect(...))(Component) — keep their prop types.
type AnyFunction = (...args: never[]) => unknown

export function compose(): <T>(arg: T) => T
export function compose<F extends AnyFunction>(f: F): F
export function compose<A, T extends unknown[], R>(f1: (a: A) => R, f2: (...args: T) => A): (...args: T) => R
export function compose<A, B, T extends unknown[], R>(
  f1: (b: B) => R,
  f2: (a: A) => B,
  f3: (...args: T) => A
): (...args: T) => R
export function compose<A, B, C, T extends unknown[], R>(
  f1: (c: C) => R,
  f2: (b: B) => C,
  f3: (a: A) => B,
  f4: (...args: T) => A
): (...args: T) => R
export function compose(...funcs: Array<(...args: unknown[]) => unknown>): (...args: unknown[]) => unknown
export function compose(...funcs: Array<(...args: unknown[]) => unknown>): (...args: unknown[]) => unknown {
  if (funcs.length === 0) {
    return arg => arg
  }

  if (funcs.length === 1) {
    return funcs[0]
  }

  return funcs.reduce(
    (a, b) =>
      (...args) =>
        a(b(...args))
  )
}

export default compose
